import { BehaviorSubject, interval, Subject, Subscription } from "rxjs";
import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import {
  EmailData,
  PerformanceRow,
  PerformanceLocation,
  LocationProgressStatus,
  PerformanceIncidentCiLog,
  PerformanceLocationResponse,
} from "src/app/store/models/performance-data.model";
import { HttpService } from "../http-service/http-client.service";
import { CheckboxCustomEvent } from "@ionic/angular";

@Injectable({
  providedIn: "root",
})
export class CITimerService {
  emailSentCount = 0;
  emailFailedCount = 0;
  emailTotalCount = 0;
  showEmailProgressBar = false;
  currentTab: string = "group";
  private tabProgressState = new Map<string, { totalValue: number; currentValue: number; failed_count: number }>();
  selectedSerch: string[] = [];
  emailConfig: EmailData[] = [];
  private TOTAL_SECONDS = 15 * 60;
  tableData: PerformanceRow[] = [];
  private intervalSub?: Subscription;
  showSendAllEmailBtn: boolean = false;
  showSendEmailSucessIcon: boolean = false;
  private startTime: number | null = null;
  showSendAllProgressBar: boolean = false;
  private tabProgressBarVisible = new Map<string, boolean>();
  public isAddModalOpen = new BehaviorSubject<boolean>(false);
  public selectedTab = new BehaviorSubject<string | null>(null);
  public refreshData$ = new Subject<void>();
  public selectedMonth = new BehaviorSubject<string | null>(null);
  isReportstepCompleted = new BehaviorSubject<boolean>(false);
  getTabProgressBarVisible(tab: string): boolean {
    return this.tabProgressBarVisible.get(tab) ?? false;
  }

  setTabProgressBarVisible(tab: string, visible: boolean): void {
    this.tabProgressBarVisible.set(tab, visible);
  }
  private httpService = inject(HttpService);
  private _monthYearDisabled = signal(false);
  public sendBulkEmailUserMsg: Boolean = false;
  public isBulkEmailSending: boolean = false;
  loader$ = new BehaviorSubject<boolean>(false);
  isShowEnv: WritableSignal<boolean> = signal(false);
  isShowTimer$ = new BehaviorSubject<boolean>(false);
  isShowHealth: WritableSignal<boolean> = signal(false);
  environmentIncidents: PerformanceIncidentCiLog[] = [];
  healthSafetyIncidents: PerformanceIncidentCiLog[] = [];
  displayTime$ = new BehaviorSubject<string | null>(null);
  isTimmerCompleted$ = new BehaviorSubject<boolean>(false);
  monthYearDisabled = this._monthYearDisabled.asReadonly();
  completedTime$ = new BehaviorSubject<string | null>(null);
  selectedBulkSerch: { type?: string; name: string }[] = [];
  public tabStateStorage = new Map<string, PerformanceRow[]>();
  originalEnvironmentIncidents: PerformanceIncidentCiLog[] = [];
  private tabSendEmailEnabledState = new Map<string, boolean>();
  originalHealthSafetyIncidents: PerformanceIncidentCiLog[] = [];
  private tabGenerationSuccessState = new Map<string, boolean>();
  private tabReportGenerationStatus = new Map<string, boolean>();
  private tabGenerationDisabledState = new Map<string, boolean>();
  attachments: { name: string; url: string; search: string; isPdf?: boolean }[] = [];
  private tabEnabledSendEmailSearches = new Map<string, Set<string>>();
  timerStarted: { [key: number]: boolean } = { 1: false, 2: false };
  private tabAttachmentsStorage = new Map<
    string,
    { name: string; url: string; search: string; isPdf?: boolean }[]
  >();
  // MODIFIED: Track successfully sent emails
  private sentEmailSearches: Set<string> = new Set();

  readonly locationProgressStore = new Map<string, LocationProgressStatus>();
  readonly emailProgressStore = new Map<string, LocationProgressStatus>();
  readonly locationSendEmailStore = new Map<string, boolean>();

  setEmailProgress(name: string, status: LocationProgressStatus): void {
    this.emailProgressStore.set(name.toLowerCase(), status);
  }

  getEmailProgress(name: string): LocationProgressStatus | undefined {
    return this.emailProgressStore.get(name.toLowerCase());
  }

  setLocationSendEmail(name: string, value: boolean): void {
    this.locationSendEmailStore.set(name?.toLowerCase(), value);
  }

  getLocationSendEmail(name: string): boolean {
    return this.locationSendEmailStore.get(name?.toLowerCase()) ?? false;
  }
  disableMonthYear() {
    this._monthYearDisabled.set(true);
  }
  enableMonthYear() {
    this._monthYearDisabled.set(false);
  }
  setIsShowTimmer(value: boolean) {
    this.isShowTimer$.next(value);
  }

  constructor() {
    this.getAllPerformanceLocationData("group");

    document.addEventListener("click", () => {
      if (this.isTimmerCompleted$.value && this.isShowTimer$.value) {
        this.isShowTimer$.next(false);
      }
    });
  }

  setEnv(value: boolean) {
    this.isShowEnv.set(value);
  }

  sethealth(value: boolean) {
    this.isShowHealth.set(value);
  }

  // Start the timer
  startTimer(): void {
    this.startTime = Date.now();
    this.loader$.next(true);
    this.completedTime$.next(null);
    this.isTimmerCompleted$.next(false);

    this.beginCountdown();
  }

  // Resume timer if already started
  resumeTimer(): void {
    if (this.startTime && this.loader$.value) {
      this.beginCountdown();
    }
  }

  // Stop timer and record completed time
  completeTimer(): void {
    if (!this.startTime) return;

    const elapsed = Math.floor((Date.now() - this.startTime) / 1000);
    const hoursRemaining = Math.floor(elapsed / 3600);
    const minutesRemaining = Math.floor((elapsed % 3600) / 60);
    const secondsRemaining = elapsed % 60;
    const formatted = `${String(hoursRemaining).padStart(2, "0")}:${String(minutesRemaining).padStart(2, "0")}:${String(secondsRemaining).padStart(2, "0")}`;

    this.completedTime$.next(formatted);
    this.isTimmerCompleted$.next(true);
    this.loader$.next(false);

    this.stopTimer();
    this.startTime = null;
  }

  // Internal countdown logic
  private beginCountdown(): void {
    this.stopTimer(); // clear  existing
    const TOTAL = this.TOTAL_SECONDS;

    this.intervalSub = interval(1000).subscribe(() => {
      if (!this.startTime) return;
      const elapsed = Math.floor((Date.now() - this.startTime) / 1000);
      const remaining = Math.max(TOTAL - elapsed, 0);

      const hoursRemaining = Math.floor(remaining / 3600);
      const minutesRemaining = Math.floor((remaining % 3600) / 60);
      const secondsRemaining = remaining % 60;
      const formatted = `${String(hoursRemaining).padStart(2, "0")}:${String(minutesRemaining).padStart(2, "0")}:${String(secondsRemaining).padStart(2, "0")}`;

      this.displayTime$.next(formatted);

      if (remaining <= 0) {
        this.loader$.next(false);
        this.stopTimer();
      }
    });
  }

  // Stop & cleanup
  stopTimer(): void {
    this.intervalSub?.unsubscribe();
    this.intervalSub = undefined;
  }

  // Reset service state completely
  reset(): void {
    this.stopTimer();
    this.loader$.next(false);
    this.displayTime$.next(null);
    this.completedTime$.next(null);
    this.startTime = null;
  }

  getAllPerformanceLocationData(type: string, onComplete?: () => void) {
    this.httpService.getAllPerformanceLocationBogPro(type)?.subscribe({
      next: (resp: PerformanceLocationResponse) => {
        if (resp?.data) {
          const newTableData = resp?.data?.map((item: PerformanceLocation) => ({
            searches: item?.name,
            type: item?.type,
            name: item.name,
            region: item?.region ?? null,
            consolidated: item?.consolidated ?? null,
            checked: false,
            downloadLinks: "",
            progress: 0,
          }));

          this.tableData = this.restoreTabState(type, newTableData);
          const savedAttachments = this.tabAttachmentsStorage.get(type);
          if (savedAttachments) {
            this.attachments = JSON.parse(JSON.stringify(savedAttachments));
          } else {
            this.attachments = [];
          }

          const savedDisabledState = this.tabGenerationDisabledState.get(type);
          const savedSuccessState = this.tabGenerationSuccessState.get(type);
          this.generateBulkReportBtnDisabled = savedDisabledState ?? false;
          this.isGenerateSucess = savedSuccessState ?? false;
          this.updateSelectedSearches();
        }

        // Call completion callback
        if (onComplete) {
          onComplete();
        }
      },
      error: (err: unknown) => {
        // Call completion callback even on error
        if (onComplete) {
          onComplete();
        }
        return err;
      },
    });
  }

  saveCurrentTabState(): void {
    if (this.currentTab && this.tableData.length > 0) {
      this.tabStateStorage.set(
        this.currentTab,
        JSON.parse(JSON.stringify(this.tableData)),
      );

      if (this.attachments.length > 0) {
        this.tabAttachmentsStorage.set(
          this.currentTab,
          JSON.parse(JSON.stringify(this.attachments)),
        );
      }

      this.tabGenerationDisabledState.set(
        this.currentTab,
        this.generateBulkReportBtnDisabled,
      );
      this.tabGenerationSuccessState.set(
        this.currentTab,
        this.isGenerateSucess,
      );

      this.tabSendEmailEnabledState.set(
        this.currentTab,
        this.disabledSendEmailBtn,
      );

      this.tabProgressState.set(this.currentTab, {
        totalValue: this.totalValue,
        currentValue: this.currentValue,
        failed_count: this.failed_count,
      });

      const currentEnabledSearches = this.tabEnabledSendEmailSearches.get(
        this.currentTab,
      );
      if (currentEnabledSearches && currentEnabledSearches.size > 0) {
        this.tabEnabledSendEmailSearches.set(
          this.currentTab,
          new Set(currentEnabledSearches),
        );
      }
    }
  }

  private restoreTabState(
    tabType: string,
    newData: PerformanceRow[],
  ): PerformanceRow[] {
    const savedState = this.tabStateStorage.get(tabType);

    if (!savedState || savedState.length === 0) {
      return newData.map((newRow) => {
        const storedStatus = this.locationProgressStore.get(
          newRow?.name?.toLowerCase?.(),
        );
        return {
          ...newRow,
          progressStatus: storedStatus,
        };
      });
    }

    const savedStateMap = new Map<string, PerformanceRow>();
    savedState.forEach((row) => {
      savedStateMap.set(row?.name?.toLowerCase?.(), row);
    });

    return newData.map((newRow) => {
      const savedRow = savedStateMap.get(newRow?.name?.toLowerCase?.());
      const storedStatus = this.locationProgressStore.get(
        newRow?.name?.toLowerCase?.(),
      );

      if (savedRow) {
        return {
          ...newRow,
          checked: savedRow.checked,
          progress: savedRow.progress,
          signedUrl: savedRow.signedUrl,
          downloadLinks: savedRow.downloadLinks,
          progressStatus: storedStatus || savedRow.progressStatus,
          lastReportAt: savedRow.lastReportAt,
          lastEmailAt: savedRow.lastEmailAt,
          hasPdf: savedRow.hasPdf,
        };
      }
      return {
        ...newRow,
        progressStatus: storedStatus,
      };
    });
  }
  clearTabStates(): void {
    this.tabStateStorage.clear();
    this.tabAttachmentsStorage.clear();
    this.tabGenerationDisabledState.clear();
    this.tabGenerationSuccessState.clear();
    this.tabSendEmailEnabledState.clear();
    this.tabEnabledSendEmailSearches.clear();
    this.tabReportGenerationStatus.clear();
    this.tabProgressState.clear();
  }

  get generateBulkReportBtnDisabled(): boolean {
    return this.tabGenerationDisabledState.get(this.currentTab) ?? false;
  }

  set generateBulkReportBtnDisabled(value: boolean) {
    this.tabGenerationDisabledState.set(this.currentTab, value);
  }

  setGenerateBulkReportBtnDisabledForTab(tab: string, value: boolean): void {
    this.tabGenerationDisabledState.set(tab, value);
  }

  get isGenerateSucess(): boolean {
    return this.tabGenerationSuccessState.get(this.currentTab) ?? false;
  }

  set isGenerateSucess(value: boolean) {
    this.tabGenerationSuccessState.set(this.currentTab, value);
  }

  setIsGenerateSucessForTab(tab: string, value: boolean): void {
    this.tabGenerationSuccessState.set(tab, value);
  }

  get disabledSendEmailBtn(): boolean {
    return this.tabSendEmailEnabledState.get(this.currentTab) ?? false;
  }

  set disabledSendEmailBtn(value: boolean) {
    this.tabSendEmailEnabledState.set(this.currentTab, value);
  }

  setDisabledSendEmailBtnForTab(tab: string, value: boolean): void {
    this.tabSendEmailEnabledState.set(tab, value);
  }

  get reportGenerationStatus(): boolean {
    return this.tabReportGenerationStatus.get(this.currentTab) ?? false;
  }

  set reportGenerationStatus(value: boolean) {
    this.tabReportGenerationStatus.set(this.currentTab, value);
  }

  setReportGenerationStatusForTab(tab: string, value: boolean): void {
    this.tabReportGenerationStatus.set(tab, value);
  }

  get totalValue(): number {
    return this.tabProgressState.get(this.currentTab)?.totalValue ?? 0;
  }

  set totalValue(value: number) {
    const current = this.tabProgressState.get(this.currentTab) || { totalValue: 0, currentValue: 0, failed_count: 0 };
    this.tabProgressState.set(this.currentTab, { ...current, totalValue: value });
  }

  get currentValue(): number {
    return this.tabProgressState.get(this.currentTab)?.currentValue ?? 0;
  }

  set currentValue(value: number) {
    const current = this.tabProgressState.get(this.currentTab) || { totalValue: 0, currentValue: 0, failed_count: 0 };
    this.tabProgressState.set(this.currentTab, { ...current, currentValue: value });
  }

  get failed_count(): number {
    return this.tabProgressState.get(this.currentTab)?.failed_count ?? 0;
  }

  set failed_count(value: number) {
    const current = this.tabProgressState.get(this.currentTab) || { totalValue: 0, currentValue: 0, failed_count: 0 };
    this.tabProgressState.set(this.currentTab, { ...current, failed_count: value });
  }

  setTabProgress(tab: string, totalValue: number, currentValue: number, failed_count: number): void {
    this.tabProgressState.set(tab, { totalValue, currentValue, failed_count });
  }

  getTabTotalValue(tab: string): number {
    return this.tabProgressState.get(tab)?.totalValue ?? 0;
  }

  get enabledSendEmailSearches(): Set<string> {
    if (!this.tabEnabledSendEmailSearches.has(this.currentTab)) {
      this.tabEnabledSendEmailSearches.set(this.currentTab, new Set<string>());
    }
    return this.tabEnabledSendEmailSearches.get(this.currentTab)!;
  }

  addEnabledSendEmailSearch(search: string): void {
    this.enabledSendEmailSearches.add(search.toLowerCase());
  }

  hasEnabledSendEmailSearch(search: string): boolean {
    return this.enabledSendEmailSearches.has(search.toLowerCase());
  }

  updateSelectedSearches(): void {
    this.selectedSerch = this.tableData
      .filter((row) => row.checked)
      .map((row) => row.searches);

    this.selectedBulkSerch = this.tableData
      ?.filter((row) => row.checked)
      ?.map((row) => ({
        type: row.type,
        name: row.name,
      }));
  }

  getSelectedCount(): number {
    return this.tableData?.filter((row) => row.checked)?.length;
  }

  toggleAll(event: CheckboxCustomEvent<boolean>): void {
    const isChecked = event?.detail?.checked;

    this.tableData.forEach((row) => {
      row.checked = isChecked;
    });

    if (!isChecked) {
      this.selectedSerch = [];
    } else {
      this.selectedSerch = this.tableData?.map((row) => row.searches);
      // this.generateBulkEmailPayload();
      this.selectedBulkSerch = this.tableData?.map((row) => ({
        type: row.type,
        name: row.name,
      }));
    }
  }

  findRowInTab(
    tabType: string,
    searchNormalized: string,
  ): PerformanceRow | undefined {
    if (tabType === this.currentTab) {
      return this.tableData?.find(
        (row) => row.searches?.toLowerCase() === searchNormalized,
      );
    }
    const savedState = this.tabStateStorage.get(tabType);
    if (!savedState) return undefined;

    return savedState.find(
      (row) => row.searches?.toLowerCase() === searchNormalized,
    );
  }

  setAttachmentsForSearch(
    search: string,
    files: { name: string; url: string; isPdf?: boolean }[],
  ): void {
    const searchLower = search.toLowerCase();
    this.attachments = this.attachments.filter(
      (att) => att.search?.toLowerCase() !== searchLower,
    );
    const saved = this.tabAttachmentsStorage.get(this.currentTab);
    if (saved) {
      this.tabAttachmentsStorage.set(
        this.currentTab,
        saved.filter((att) => att.search?.toLowerCase() !== searchLower),
      );
    }
    // Only PDF reports should ever be offered as email attachments — the bucket folder
    // also contains the raw response.json metrics data used to render the report, which
    // must never be attached/sent.
    files
      .filter((f) => f.isPdf)
      .forEach((f) =>
        this.attachments.push({ name: f.name, url: f.url, search, isPdf: f.isPdf }),
      );
  }

  addAttachmentToTab(
    tabType: string,
    attachment: { name: string; url: string; search: string; isPdf?: boolean },
  ): void {
    if (tabType === this.currentTab) {
      const existingIndex = this.attachments.findIndex(
        (att) => att.search === attachment.search,
      );
      if (existingIndex !== -1) {
        this.attachments[existingIndex] = attachment;
      } else {
        this.attachments.push(attachment);
      }
    } else {
      const savedAttachments = this.tabAttachmentsStorage.get(tabType) || [];
      const existingIndex = savedAttachments.findIndex(
        (att) => att.search === attachment.search,
      );
      if (existingIndex !== -1) {
        savedAttachments[existingIndex] = attachment;
      } else {
        savedAttachments.push(attachment);
      }
      this.tabAttachmentsStorage.set(tabType, savedAttachments);
    }
  }

  addEnabledSendEmailSearchToTab(tabType: string, search: string): void {
    if (tabType === this.currentTab) {
      this.addEnabledSendEmailSearch(search);
    } else {
      if (!this.tabEnabledSendEmailSearches.has(tabType)) {
        this.tabEnabledSendEmailSearches.set(tabType, new Set<string>());
      }
      const enabledSearches = this.tabEnabledSendEmailSearches.get(tabType)!;
      enabledSearches.add(search.toLowerCase());
    }
  }

  // MODIFIED: Check if search email was sent successfully
  isEmailSent(search: string): boolean {
    return this.sentEmailSearches.has(search.toLowerCase());
  }

  // MODIFIED: Mark search as sent
  markEmailAsSent(search: string): void {
    this.sentEmailSearches.add(search.toLowerCase());
  }

  // MODIFIED: Clear sent emails
  clearSentEmails(): void {
    this.sentEmailSearches.clear();
  }

  clearEmailProgressStore(): void {
    this.emailProgressStore.clear();
  }

  clearNewJobState(): void {
    this.sentEmailSearches.clear();
    this.emailProgressStore.clear();
  }

  setLocationProgress(name: string, status: LocationProgressStatus): void {
    const normalizedName = name.toLowerCase();
    this.locationProgressStore.set(normalizedName, status);
    if (status === 'queued' || status === 'generating_report') {
      this.emailProgressStore.delete(normalizedName);
      this.sentEmailSearches.delete(normalizedName);
    }
    const row = this.tableData.find(
      (_row) => _row.searches?.toLowerCase() === normalizedName,
    );
    if (row) {
      row.progressStatus = status;
    }
    this.tabStateStorage.forEach((tabRows, tabName) => {
      const tabRow = tabRows.find(
        (_row) => _row.searches?.toLowerCase() === normalizedName,
      );
      if (tabRow) {
        tabRow.progressStatus = status;
      }
    });
  }

  getLocationProgress(name: string): LocationProgressStatus | undefined {
    return this.locationProgressStore.get(name.toLowerCase());
  }

  clearLocationProgressStore(): void {
    this.locationProgressStore.clear();
    this.tableData.forEach((_row) => (_row.progressStatus = undefined));
  }

  // CITimerService — add this method
  isTimerActive(): boolean {
    return this.startTime !== null && this.loader$.value === true;
  }
}
